import type {
  CameraRequest,
  CreateRequestInput,
  RequestDecision,
  RequestStatus,
} from "@/domains/requests/api/types";
import { SEED_REQUESTS } from "@/domains/requests/mocks/requests";

let requests: Array<CameraRequest> = SEED_REQUESTS.map((r) => ({ ...r }));
let nextNumber = 9231;

function delay<T>(value: T, ms = 200): Promise<T> {
  return new Promise((resolve) => setTimeout(() => resolve(value), ms));
}

export async function fetchRequests(): Promise<ReadonlyArray<CameraRequest>> {
  return delay(requests);
}

export async function fetchRequestsByRequester(
  requesterId: string,
): Promise<ReadonlyArray<CameraRequest>> {
  return delay(requests.filter((r) => r.requesterId === requesterId));
}

export async function fetchRequestById(id: string): Promise<CameraRequest | undefined> {
  return delay(requests.find((r) => r.id === id));
}

export async function createRequest(input: CreateRequestInput): Promise<CameraRequest> {
  const id = `req-${nextNumber}`;
  const created: CameraRequest = {
    ...input,
    id,
    number: `AANV-2026-${nextNumber}`,
    status: "submitted",
    submittedAt: new Date().toISOString(),
  };
  requests = [created, ...requests];
  nextNumber += 1;
  return delay(created, 350);
}

export async function updateRequestStatus(
  id: string,
  status: RequestStatus,
): Promise<CameraRequest> {
  const idx = requests.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error(`Request niet gevonden: ${id}`);
  const updated: CameraRequest = { ...requests[idx], status };
  requests = [...requests.slice(0, idx), updated, ...requests.slice(idx + 1)];
  return delay(updated, 150);
}

export async function linkRequestToCase(id: string, caseId: string): Promise<CameraRequest> {
  const idx = requests.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error(`Request niet gevonden: ${id}`);
  const updated: CameraRequest = { ...requests[idx], caseId, status: "approved" };
  requests = [...requests.slice(0, idx), updated, ...requests.slice(idx + 1)];
  return delay(updated, 150);
}

type DecisionInput = {
  id: string;
  decision: Omit<RequestDecision, "at">;
  status: RequestStatus;
  caseId?: string;
};

export async function appendRequestDecision({
  id,
  decision,
  status,
  caseId,
}: DecisionInput): Promise<CameraRequest> {
  const idx = requests.findIndex((r) => r.id === id);
  if (idx === -1) throw new Error(`Request niet gevonden: ${id}`);
  const current = requests[idx];
  const newDecision: RequestDecision = {
    ...decision,
    at: new Date().toISOString(),
  };
  const updated: CameraRequest = {
    ...current,
    status,
    caseId: caseId ?? current.caseId,
    decisions: [...(current.decisions ?? []), newDecision],
  };
  requests = [...requests.slice(0, idx), updated, ...requests.slice(idx + 1)];
  return delay(updated, 150);
}
